import { useState } from 'react';
import {
  ApartmentOutlined,
  TeamOutlined,
  WarningOutlined,
  DownloadOutlined,
  EyeOutlined,
  FolderOpenOutlined,
  FolderOutlined,
  ReloadOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { FolderKanban } from 'lucide-react';

import { Bento, DataTable, PageHeader, StatTile, Tile } from '../../../components/shared';
import { Button, Dialog, Empty, Input, Skeleton } from '../../../components/ui';
import { useLoi } from '../../../hooks/useLoi';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { KHONG_CO, ngayGioVN, ngayVN, soVN } from '../../../lib/dinh-dang';
import { luuTep } from '../../../lib/luu-tep';
import { useToast } from '../../../lib/toast';
import { KhongCoQuyen, LoiRpa } from '../components/chung';
import { BadgeLoaiLicense, BadgeTrangThaiLicense, HanLicense } from '../components/license/nhan';
import { QUYEN } from '../constants';
import { useLicenses } from '../hooks/useLicense';
import { useLamMoiProjects, useProjects } from '../hooks/useOpenCore';
import { kpiLicenseProject, licenseCuaProject, loaiLicense, NGAY_SAP_HET_HAN, trangThaiUser } from '../nghiep-vu/license';
import { ngayChoTenTep, taoCsv } from '../nghiep-vu/csv';
import { dongCsvProject, locProject, tongWorkflow } from '../nghiep-vu/workflow';
import type { LicenseRpa, ProjectOpenRpa, WorkflowCuaProject } from '../types';

/** Số chip Workflow hiện trong một ô; thừa thì gom thành `+n`. */
const SO_CHIP = 4;

/**
 * Danh sách Project (`/rpa/projects`, `RpaProjectsView` của native): Project OpenRPA trên OpenCore của tenant kèm
 * Workflow bên trong, kèm số User được cấp License theo Project (chỉ khi có `rpa:license:read`; thiếu quyền thì ẩn cột
 * và hai ô KPI License, không hiện số giả). Tìm kiếm lọc ở máy khách; Làm mới bỏ bộ nhớ đệm 30 giây của máy chủ
 * (`?fresh=1`).
 */
export function ProjectView() {
  const { t, locale } = useT();
  const toast = useToast();
  const dichLoi = useLoi();
  const quyen = useModulePermissions(QUYEN);
  const ds = useProjects(quyen.projectDoc);
  const lic = useLicenses(quyen.projectDoc && quyen.licenseDoc);
  const licenses = quyen.licenseDoc ? lic.data?.data : undefined;
  const kpi = licenses ? kpiLicenseProject(licenses) : null;
  const lamMoi = useLamMoiProjects();
  const [tim, setTim] = useState('');
  const [dangMo, setDangMo] = useState<ProjectOpenRpa | null>(null);

  const tieuDe = t('nav.muc.danhSachProject');
  const projects = ds.data ?? [];
  const dong = locProject(projects, tim);

  const xuatCsv = () => {
    const csv = taoCsv(
      [t('rpaProject.csvTen'), t('rpaProject.csvId'), t('rpaProject.csvWorkflow'), t('rpaProject.csvCapNhat')],
      dongCsvProject(projects, (iso) => ngayGioVN(iso, locale)),
    );
    luuTep(new Blob([csv], { type: 'text/csv;charset=utf-8' }), `danh-sach-project-rpa-${ngayChoTenTep(new Date())}.csv`);
    toast.success(t('rpaProject.daXuat'));
  };

  return (
    <div className="space-y-4 p-4 lg:p-6">
      <PageHeader
        breadcrumb={[{ label: t('rpa.nhom') }, { label: t('nav.muc.quanLyProjectWorkflow') }, { label: tieuDe }]}
        titleIcon={<FolderKanban size={18} className="text-primary-600" aria-hidden />}
        title={tieuDe}
        subtitle={t('rpaProject.moTa')}
        actions={
          quyen.projectDoc ? (
            <Button
              variant="outline"
              size="md"
              icon={<ReloadOutlined />}
              loading={lamMoi.isPending || (ds.isFetching && !ds.isPending)}
              onClick={() =>
                lamMoi.mutate(undefined, {
                  onSuccess: () => toast.success(t('rpaProject.daLamMoi')),
                  onError: (e) => toast.error(dichLoi(e)),
                })
              }
            >
              {t('chung.lamMoi')}
            </Button>
          ) : undefined
        }
      />

      {!quyen.projectDoc ? (
        <KhongCoQuyen quyen={QUYEN.projectDoc} />
      ) : ds.isPending ? (
        <div className="space-y-4" aria-busy="true">
          <Bento>
            <Tile span="sm:col-span-6 lg:col-span-3"><Skeleton className="h-20 w-full" /></Tile>
            <Tile span="sm:col-span-6 lg:col-span-3"><Skeleton className="h-20 w-full" /></Tile>
          </Bento>
          <Tile><Skeleton className="h-64 w-full" /></Tile>
        </div>
      ) : ds.isError ? (
        <Tile>
          <LoiRpa loi={ds.error} onThuLai={() => void ds.refetch()} />
        </Tile>
      ) : (
        <>
          <Bento>
            <StatTile
              nhan={t('rpaProject.tongProject')}
              so={soVN(projects.length, locale)}
              sub={t('rpaProject.tongProjectSub')}
              icon={<FolderOutlined />}
              tone="primary"
            />
            <StatTile
              nhan={t('rpaProject.tongWorkflow')}
              so={soVN(tongWorkflow(projects), locale)}
              sub={t('rpaProject.tongWorkflowSub')}
              icon={<ApartmentOutlined />}
              tone="teal"
            />
            {quyen.licenseDoc && (
              <>
                <StatTile
                  nhan={t('rpaProject.userCoLicense')}
                  so={kpi ? soVN(kpi.userCoLicense, locale) : KHONG_CO}
                  sub={lic.isError ? t('rpaProject.licenseKhongTai') : undefined}
                  icon={<TeamOutlined />}
                  tone="violet"
                />
                <StatTile
                  nhan={t('rpaProject.licenseSapHet')}
                  so={kpi ? soVN(kpi.sapHetHan, locale) : KHONG_CO}
                  sub={t('rpaProject.licenseSapHetSub', { so: soVN(NGAY_SAP_HET_HAN, locale) })}
                  icon={<WarningOutlined />}
                  tone="danger"
                />
              </>
            )}
          </Bento>

          {projects.length === 0 ? (
            <Tile>
              <Empty icon={FolderOpenOutlined} title={t('rpaProject.rong')} description={t('rpaProject.rongMoTa')} />
            </Tile>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <Input
                  allowClear
                  prefix={<SearchOutlined aria-hidden />}
                  placeholder={t('rpaProject.tim')}
                  aria-label={t('rpaProject.tim')}
                  value={tim}
                  onChange={(e) => setTim(e.target.value)}
                  className="w-full sm:w-72"
                />
                <Button variant="outline" size="md" icon={<DownloadOutlined />} onClick={xuatCsv}>
                  {t('rpaProject.xuatCsv')}
                </Button>
              </div>

              <Tile className="min-w-0">
                {dong.length === 0 ? (
                  <Empty icon={SearchOutlined} title={t('rpaProject.rongLoc')} />
                ) : (
                  <DataTable<ProjectOpenRpa>
                    caption={tieuDe}
                    dataSource={dong}
                    pagination={false}
                    columns={[
                      {
                        title: t('chung.stt'),
                        key: 'stt',
                        width: 56,
                        render: (_, _p, i) => <span className="tabular-nums">{soVN(i + 1, locale)}</span>,
                      },
                      {
                        title: t('rpaProject.cotTen'),
                        key: 'name',
                        render: (_, p) => (
                          <div className="min-w-0 max-w-xs">
                            <p className="font-semibold text-content-primary">{p.name}</p>
                            <p className="break-all font-mono text-2xs text-content-muted">{p.id}</p>
                          </div>
                        ),
                      },
                      { title: t('rpaProject.cotWorkflow'), key: 'workflows', render: (_, p) => <ChipWorkflow project={p} /> },
                      ...(quyen.licenseDoc
                        ? [
                            {
                              title: t('rpaProject.cotUserCap'),
                              key: 'licenses',
                              align: 'right' as const,
                              width: 120,
                              render: (_: unknown, p: ProjectOpenRpa) => (
                                <span className="tabular-nums">{licenses ? soVN(licenseCuaProject(licenses, p.id).length, locale) : KHONG_CO}</span>
                              ),
                            },
                          ]
                        : []),
                      {
                        title: t('rpaProject.cotCapNhat'),
                        key: 'updatedAt',
                        width: 160,
                        render: (_, p) => <span className="whitespace-nowrap text-xs tabular-nums">{ngayGioVN(p.updatedAt, locale)}</span>,
                      },
                      {
                        title: <span className="sr-only">{t('chung.thaoTac')}</span>,
                        key: 'thaoTac',
                        width: 72,
                        align: 'center',
                        // Một hành động duy nhất ở mọi dòng: nút icon trần, không menu ⋮ (ghi chú của `MenuThaoTac`).
                        render: (_, p) => (
                          <Button
                            variant="ghost"
                            size="sm"
                            icon={<EyeOutlined />}
                            aria-label={t('rpaProject.xemChiTietCua', { ten: p.name })}
                            title={t('rpaProject.xemChiTiet')}
                            onClick={() => setDangMo(p)}
                          />
                        ),
                      },
                    ]}
                  />
                )}
              </Tile>
            </>
          )}
        </>
      )}

      {dangMo && (
        <HopProject project={dangMo} licenses={licenses ? licenseCuaProject(licenses, dangMo.id) : null} onClose={() => setDangMo(null)} />
      )}
    </div>
  );
}

function ChipWorkflow({ project }: { project: ProjectOpenRpa }) {
  const { t, locale } = useT();
  if (project.workflows.length === 0) {
    return <span className="text-xs italic text-content-muted">{t('rpaProject.chuaCoWorkflow')}</span>;
  }
  const thua = project.workflows.length - SO_CHIP;
  return (
    <div className="flex max-w-sm flex-wrap gap-1">
      {project.workflows.slice(0, SO_CHIP).map((w) => (
        <span key={w.id} className="rounded border border-default bg-subtle px-1.5 py-0.5 text-xs text-content-secondary">
          {w.name}
        </span>
      ))}
      {thua > 0 && (
        <span className="rounded bg-orange-50 px-1.5 py-0.5 text-xs text-orange-700">
          {t('rpaProject.themWorkflow', { so: soVN(thua, locale) })}
        </span>
      )}
    </div>
  );
}

/**
 * Chi tiết Project: Workflow trên OpenCore và User được cấp License trong Project. `licenses` là `null` khi người xem
 * không có `rpa:license:read` — ẩn cả cột và bảng License.
 */
function HopProject({ project, licenses, onClose }: { project: ProjectOpenRpa; licenses: LicenseRpa[] | null; onClose: () => void }) {
  const { t, locale } = useT();
  const userCua = (wfId: string) =>
    (licenses ?? [])
      .filter((l) => l.workflow_scope?.workflows.some((w) => w.id === wfId))
      .map((l) => l.opencore_username || l.opencore_user_id || KHONG_CO);
  return (
    <Dialog
      open
      onClose={onClose}
      width={880}
      title={t('rpaProject.hopTieuDe', { ten: project.name })}
      footer={
        <div className="flex justify-end">
          <Button variant="outline" size="md" onClick={onClose}>
            {t('chung.dong')}
          </Button>
        </div>
      }
    >
      <p className="mb-4 break-all text-xs text-content-muted">
        {t('rpaProject.hopMeta', { id: project.id, luc: project.updatedAt ? ngayGioVN(project.updatedAt, locale) : KHONG_CO })}
      </p>
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-content-muted">
        {t('rpaProject.hopWorkflow', { so: soVN(project.workflows.length, locale) })}
      </h3>
      {project.workflows.length === 0 ? (
        <Empty icon={ApartmentOutlined} title={t('rpaProject.hopRong')} className="py-6" />
      ) : (
        <DataTable<WorkflowCuaProject>
          caption={t('rpaProject.hopWorkflow', { so: soVN(project.workflows.length, locale) })}
          size="small"
          pagination={false}
          dataSource={project.workflows}
          columns={[
            { title: t('rpaProject.hopCotWorkflow'), key: 'name', render: (_, w) => <span className="font-medium">{w.name}</span> },
            { title: t('rpaProject.hopCotId'), key: 'id', render: (_, w) => <code className="break-all font-mono text-xs">{w.id}</code> },
            ...(licenses
              ? [
                  {
                    title: t('rpaProject.hopCotUser'),
                    key: 'users',
                    render: (_: unknown, w: WorkflowCuaProject) => {
                      const u = userCua(w.id);
                      return u.length ? (
                        <span className="line-clamp-2 max-w-[14rem] text-xs" title={u.join(', ')}>
                          {u.join(', ')}
                        </span>
                      ) : (
                        <span className="text-xs italic text-content-muted">{t('rpaProject.hopChuaCap')}</span>
                      );
                    },
                  },
                ]
              : []),
            {
              title: t('rpaProject.hopCotCapNhat'),
              key: 'updatedAt',
              width: 120,
              render: (_, w) => <span className="whitespace-nowrap text-xs tabular-nums">{ngayVN(w.updatedAt)}</span>,
            },
          ]}
        />
      )}
      {licenses && <BangLicenseProject licenses={licenses} />}
    </Dialog>
  );
}

function BangLicenseProject({ licenses }: { licenses: LicenseRpa[] }) {
  const { t, locale } = useT();
  return (
    <section className="mt-6">
      <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-content-muted">
        {t('rpaProject.hopLicense', { so: soVN(licenses.length, locale) })}
      </h3>
      {licenses.length === 0 ? (
        <Empty icon={TeamOutlined} title={t('rpaProject.hopChuaCoLicense')} description={t('rpaProject.hopChuaCoLicenseMoTa')} className="py-6" />
      ) : (
        <DataTable<LicenseRpa>
          caption={t('rpaProject.hopLicense', { so: soVN(licenses.length, locale) })}
          size="small"
          pagination={false}
          rowKey="id"
          dataSource={licenses}
          columns={[
            {
              title: t('rpaProject.hopCotUserLicense'),
              key: 'user',
              render: (_, l) => (
                <div className="min-w-0 max-w-[12rem] leading-tight">
                  <p className="truncate font-medium">{l.opencore_username || l.opencore_user_id || KHONG_CO}</p>
                  {l.opencore_email && <p className="truncate text-xs text-content-muted">{l.opencore_email}</p>}
                </div>
              ),
            },
            { title: t('rpaProject.hopCotLoai'), key: 'loai', render: (_, l) => <BadgeLoaiLicense loai={loaiLicense(l)} /> },
            {
              title: t('rpaProject.hopCotPhamVi'),
              key: 'phamVi',
              render: (_, l) => {
                const wf = l.workflow_scope?.workflows ?? [];
                return (
                  <span className="block max-w-[14rem] truncate text-xs" title={wf.map((w) => w.name).join(', ')}>
                    {wf.length === 1 ? wf[0]!.name : t('rpaLicense.soWorkflow', { so: soVN(wf.length, locale) })}
                  </span>
                );
              },
            },
            { title: t('rpaProject.hopCotTrangThai'), key: 'status', render: (_, l) => <BadgeTrangThaiLicense status={trangThaiUser(l)} /> },
            { title: t('rpaProject.hopCotHetHan'), key: 'han', render: (_, l) => <HanLicense license={l} /> },
          ]}
        />
      )}
    </section>
  );
}
